import React, { useCallback, useMemo, useState } from 'react';
import { useInforStore } from '../store/clickStore.js';
import { useShallow } from 'zustand/react/shallow';
import { useCompactLayout } from '../hooks/useCompactLayout';

function parseInfo(raw, pick) {
  if (pick?.name) {
    const east = Number(pick.east);
    const north = Number(pick.north);
    const rl = Number(pick.rl);
    const hasCoords =
      Number.isFinite(east) && Number.isFinite(north) && Number.isFinite(rl);
    return {
      name: pick.name,
      east: hasCoords ? east : null,
      north: hasCoords ? north : null,
      rl: hasCoords ? rl : null,
    };
  }
  if (!raw || raw === '...') return null;
  const text = String(raw);
  const mineMatch = text.match(
    /coords:\s*E:([-\d.]+)\s*N:([-\d.]+)\s*RL:([-\d.]+)/i
  );
  const xyzMatch = text.match(/coords:\s*X:([-\d.]+)\s*Y:([-\d.]+)\s*Z:([-\d.]+)/i);
  const name = text.replace(/\s*coords:.*$/i, '').trim();
  if (mineMatch) {
    return {
      name: name || 'Object',
      east: Number(mineMatch[1]),
      north: Number(mineMatch[2]),
      rl: Number(mineMatch[3]),
    };
  }
  if (xyzMatch) {
    return {
      name: name || 'Object',
      east: Number(xyzMatch[1]),
      north: Number(xyzMatch[2]),
      rl: Number(xyzMatch[3]),
    };
  }
  return { name: name || text, east: null, north: null, rl: null };
}

function formatCoord(n) {
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function formatCoords(parsed) {
  return `E:${formatCoord(parsed.east)}  N:${formatCoord(parsed.north)}  RL:${formatCoord(parsed.rl)}`;
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

export default function Info() {
  const compact = useCompactLayout();
  const [copied, setCopied] = useState(false);
  const { info, pick } = useInforStore(
    useShallow((state) => ({
      info: state.info,
      pick: state.pick,
    }))
  );

  const parsed = useMemo(() => parseInfo(info, pick), [info, pick]);

  const handleCopy = useCallback(async () => {
    if (!parsed || parsed.east == null) return;
    const ok = await copyText(formatCoords(parsed));
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }, [parsed]);

  if (!parsed) return null;

  const hasCoords = parsed.east != null;

  return (
    <div
      role="status"
      className={`pointer-events-auto absolute z-[80] left-1/2 -translate-x-1/2 inline-flex w-max max-w-[calc(100%-1.5rem)] items-center gap-x-2.5 rounded border border-yellow-600/40 bg-black/92 px-2 py-0.5 shadow-[0_6px_20px_rgba(0,0,0,0.5)] backdrop-blur-md ${
        compact ? 'bottom-16' : 'bottom-3'
      }`}
    >
      <p className="m-0 whitespace-nowrap text-[11px] font-medium text-yellow-400 leading-none">
        {parsed.name}
      </p>
      {hasCoords && (
        <>
          <p className="m-0 flex items-center gap-x-2 whitespace-nowrap text-[10px] tabular-nums text-zinc-400 leading-none">
            <span>
              E <span className="text-zinc-200">{formatCoord(parsed.east)}</span>
            </span>
            <span>
              N <span className="text-zinc-200">{formatCoord(parsed.north)}</span>
            </span>
            <span>
              RL <span className="text-zinc-200">{formatCoord(parsed.rl)}</span>
            </span>
          </p>
          <button
            type="button"
            title="Copy coordinates"
            className="m-0 h-5 shrink-0 rounded-sm border border-yellow-600/35 px-1.5 text-[8px] uppercase tracking-wider text-yellow-400 hover:bg-yellow-500/10"
            onClick={handleCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </>
      )}
    </div>
  );
}
